import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueries, useMutation, useQueryClient } from '@tanstack/react-query';
import ProduktKonfigFelder, { fehlendePflicht, startWerte } from './ProduktKonfigFelder';
import VerbundZusatzFelder, { type VerbundZusatz } from './VerbundZusatzFelder';
import { X, AlertTriangle, Info, Link2, Globe } from 'lucide-react';
import DomainAuswahl from './DomainAuswahl';
import { instances, customers, products, servers, domains, demoSaatgut, webseiten, applications } from '../api';
import DnsAnleitung from './DnsAnleitung';
import { UMGEBUNGEN, umgebung as umgebungDef } from '../umgebungen';
import type { InstanceEnvironment, ProductConfigField } from '../types';

/**
 * Eine Instanz von Hand anlegen.
 *
 * WARUM ES DAS BRAUCHT: Bis hierher gab es keinen Weg über die Oberfläche.
 * `instances.create` stand in der API und wurde von nichts aufgerufen — jede
 * Instanz entstand über eine Bestellung oder von Hand am Server. Für eine
 * Abnahme- oder Zweitinstanz, hinter der keine Bestellung steht, fehlte damit
 * jeder Weg.
 *
 * WAS ES NICHT TUT: ausrollen. Angelegt wird ein Datensatz; die Container
 * entstehen erst beim Ausrollen. Das ist absichtlich getrennt — so lässt sich
 * eine Instanz vorbereiten, prüfen und erst dann in Betrieb nehmen. Der
 * Dialog sagt das auch, statt den Eindruck zu erwecken, es liefe schon etwas.
 */

type Betriebsart = 'MANAGED' | 'SELF_HOSTED';

/** Nicht verkaeufliche Produkte, die trotzdem in den Instanzreiter gehoeren. */
const IM_INSTANZREITER = ['elibrary', 'filemaker'];

export default function InstanzAnlegenModal({
  onClose,
  art = 'PRODUKT',
}: {
  onClose: () => void;
  /** Produktinstanz, App oder Webseite — siehe docs/KONZEPT-APPS-UND-WEBSEITEN.md. */
  art?: 'PRODUKT' | 'APP' | 'WEBSEITE';
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [fehler, setFehler] = useState('');

  const [customerId, setCustomerId] = useState('');
  const [productId, setProductId] = useState('');
  /** Demo-Inhalte, die nach der Bereitstellung eingespielt werden sollen. */
  const [demoSeedIds, setDemoSeedIds] = useState<string[]>([]);
  /**
   * Passwort des Betreiberkontos.
   *
   * Leer heisst: der PC erzeugt eins — und dann sieht es niemand, weil erzeugte
   * Geheimnisse nicht angezeigt werden. Wer spaeter hineinmuss, erzeugt an der
   * Instanz ein neues. Deshalb hier gleich ein Vorschlag, den man einmal vor
   * Augen hat und ablegen kann.
   */
  const [adminPasswort, setAdminPasswort] = useState('');
  const [passwortSichtbar, setPasswortSichtbar] = useState(false);
  // Ohne Adresse legt die Instanz das Betreiberkonto gar nicht erst an — und
  // in der Oberflaeche stand bisher nur „nicht gesetzt", ohne Weg dorthin.
  const [adminEmail, setAdminEmail] = useState('admin@juni.com');
  const [kopiert, setKopiert] = useState(false);
  /** Nur bei Webseiten: die eingefuegte compose.yaml und die .env dazu. */
  const [composeInline, setComposeInline] = useState('');
  const [envInline, setEnvInline] = useState('');
  const [zielDienst, setZielDienst] = useState('');
  const [zielPort, setZielPort] = useState('');
  const [planId, setPlanId] = useState('');
  const [hostingMode, setHostingMode] = useState<Betriebsart>('MANAGED');
  const [serverId, setServerId] = useState('');
  const [templateId, setTemplateId] = useState('');
  /** Welche Application des Produkts — nur bei Bündeln zu wählen (Stufe 2). */
  const [applicationId, setApplicationId] = useState('');
  const [domain, setDomain] = useState('');
  const [subdomain, setSubdomain] = useState('');
  const [stackName, setStackName] = useState('');
  const [releaseChannel, setReleaseChannel] = useState('stable');

  /**
   * Unsere Domain oder die des Kunden.
   *
   * Der Unterschied ist nicht kosmetisch: bei einer Domain in unserer
   * Cloudflare-Zone legt der DDNS-Sidecar den A-Record selbst an und hält ihn
   * aktuell — deshalb kann eine Instanz den Server wechseln, ohne dass jemand
   * DNS anfasst. Bei einer Kundendomain muss der Kunde zeigen, und ein Umzug
   * wird zur Absprache.
   */
  const [domainQuelle, setDomainQuelle] = useState<'unsere' | 'kunde'>('unsere');

  /** Optionale Verknüpfung mit einer bestehenden Instanz desselben Kunden. */
  const [verknuepfenMit, setVerknuepfenMit] = useState('');

  /**
   * Auch Domains ohne Cloudflare anbieten.
   *
   * Voreinstellung aus: von über vierhundert Domains liegen nur wenige bei
   * Cloudflare, und nur bei denen setzt der DDNS-Container den A-Record selbst.
   * Die übrigen anzubieten hiesse, die Auswahl mit Adressen zu füllen, bei
   * denen jemand hinterher von Hand nachziehen muss — ohne dass es irgendwo
   * steht.
   */
  const [alleDomains, setAlleDomains] = useState(false);

  /**
   * Produktion oder Abnahme.
   *
   * Steckt im Namen, nicht in einem Feld: `ancora` und `ancora-stage` sagen
   * schon in `docker ps` und im Proxy, worum es sich handelt. Ein Merkmal, das
   * nur in der Datenbank steht, sieht niemand, der gerade auf dem Wirt steht.
   */
  const [umgebung, setUmgebung] = useState<InstanceEnvironment>('PRODUCTION');

  /**
   * Hat jemand die Namen selbst angefasst?
   *
   * Getrennt für beide, weil sie verschiedenen Regeln folgen: wer die Subdomain
   * anpasst, will deshalb nicht auch den Stack-Namen von Hand pflegen. Solange
   * der jeweilige Merker aus ist, folgt das Feld dem Vorschlag — danach nicht
   * mehr. Eine Automatik, die eine Eingabe von Hand wieder überschreibt, ist
   * die ärgerlichste Sorte.
   */
  const [subdomainVonHand, setSubdomainVonHand] = useState(false);
  const [stackVonHand, setStackVonHand] = useState(false);

  const { data: kunden } = useQuery({ queryKey: ['customers'], queryFn: () => customers.list() });
  const { data: produkte } = useQuery({ queryKey: ['products'], queryFn: () => products.list() });
  const { data: serverListe } = useQuery({ queryKey: ['servers'], queryFn: () => servers.list() });

  const { data: domainListe } = useQuery({
    queryKey: ['domains'],
    queryFn: () => domains.list(),
    enabled: domainQuelle === 'unsere',
  });

  /**
   * Die Instanzen dieses Kunden — Kandidaten für eine Verknüpfung.
   *
   * Nur andere Produkte: ojfm bettet open.task ein, nicht sich selbst. Das
   * prüft der Server auch, aber eine Auswahl, die unmögliche Einträge zeigt,
   * ist schlechter als eine, die sie weglässt.
   */
  const { data: kundenInstanzen } = useQuery({
    queryKey: ['instances', { customerId }],
    queryFn: () => instances.list({ customerId }),
    enabled: Boolean(customerId),
  });

  const { data: plaene } = useQuery({
    queryKey: ['products', productId, 'plans'],
    queryFn: () => products.getPlans(productId),
    enabled: Boolean(productId),
  });
  // Das Produkt samt seinen Applications (Stufe 2): Vorlagen und
  // Einstellungsfelder gehoeren der Application, Vorgaben dem Produkt.
  const { data: produktDetail } = useQuery({
    queryKey: ['products', productId, 'detail'],
    queryFn: () => products.get(productId),
    enabled: Boolean(productId),
  });
  const teile = produktDetail?.applications ?? [];
  // Ein Produkt mit genau einer Application: die ist es. Bei einem Buendel
  // waehlt man, welcher Teil diese Instanz wird.
  useEffect(() => {
    setApplicationId(teile.length === 1 ? teile[0].applicationId : '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId, teile.length]);
  const teil = teile.find((t) => t.applicationId === applicationId);
  // Manche Apps duerfen nur auf bestimmte Server (FileMaker Server: hoth).
  // Ist es genau einer, steht er gleich drin; ein vorher gewaehlter anderer
  // fliegt raus, sonst scheiterte erst das Anlegen.
  const erlaubteServer = teil?.application.erlaubteServer ?? [];
  useEffect(() => {
    if (!erlaubteServer.length || erlaubteServer.includes(serverId)) return;
    setServerId(erlaubteServer.length === 1 ? erlaubteServer[0] : '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId, erlaubteServer.join(',')]);
  const { data: vorlagen } = useQuery({
    queryKey: ['applications', applicationId, 'templates'],
    queryFn: () => applications.getTemplates(applicationId),
    enabled: Boolean(applicationId),
  });

  // Die aktive Vorlage vorbelegen. Wer eine veraltete will, kann umstellen —
  // aber die Voreinstellung soll nicht zufällig die älteste sein.
  useEffect(() => {
    if (!vorlagen?.length) return;
    const aktiv = vorlagen.find((v) => v.status === 'ACTIVE');
    setTemplateId(aktiv?.id ?? '');
  }, [vorlagen]);

  const kunde = kunden?.find((k) => k.id === customerId);
  // ══ WELCHE PRODUKTE HIER ZUR WAHL STEHEN ═════════════════════════════════
  //
  // Dieselbe Tabelle, drei Ausschnitte. Die Betriebsprodukte (`imVerkauf:
  // false`) gehoeren nicht in die Produktliste einer Kundeninstanz, und
  // umgekehrt gehoert open.junixx nicht unter „Shops und Apps".
  const auswahl = (produkte ?? []).filter((p) => {
    // Die eLibrary ist (noch) nicht im Verkauf, laeuft aber als Produktinstanz
    // bei Kunden — ohne diese Ausnahme liess sie sich hier gar nicht anlegen.
    // FileMaker Server ebenso: nicht verkaeuflich (Migrationswerkzeug), soll
    // aber im Instanzreiter verwaltet werden (Nico Mück 27.10.2026).
    if (art === 'PRODUKT') return p.imVerkauf !== false || IM_INSTANZREITER.includes(p.slug);
    if (art === 'APP') return p.imVerkauf === false && p.slug !== 'webseite' && !IM_INSTANZREITER.includes(p.slug);
    return p.slug === 'webseite';
  });

  // Bei einer Webseite gibt es nichts zu waehlen: es ist immer dasselbe
  // Sammelprodukt. Das Feld wird deshalb nicht angezeigt, sondern gesetzt.
  React.useEffect(() => {
    if (art !== 'WEBSEITE') return;
    const w = (produkte ?? []).find((p) => p.slug === 'webseite');
    if (w && productId !== w.id) setProductId(w.id);
  }, [art, produkte, productId]);

  // Was steht in der eingefuegten compose.yaml? Der PC liest es, damit die
  // Frage „welcher Dienst bekommt die Adresse" eine Auswahlliste ist. Geraten
  // wird nicht: bei genau einem Dienst waere es bequem, bei zweien still falsch.
  const { data: composeBefund } = useQuery({
    queryKey: ['compose-lesen', composeInline],
    queryFn: () => webseiten.composeLesen(composeInline),
    enabled: art === 'WEBSEITE' && composeInline.trim().length > 20,
  });

  /**
   * Ein Passwort, das durch Compose und Shell kommt.
   *
   * Ohne `$`, Anfuehrungszeichen und Backslash: der Wert landet in einer .env
   * auf dem Wirt und geht von dort durch Docker Compose. Ein `$` darin waere
   * dort eine Variable, kein Zeichen.
   */
  const passwortErzeugen = () => {
    const zeichen = 'abcdefghijkmnopqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789-_';
    const roh = new Uint32Array(24);
    crypto.getRandomValues(roh);
    return Array.from(roh, (n) => zeichen[n % zeichen.length]).join('');
  };

  // Einmal beim Oeffnen vorbelegen. Nicht bei jedem Tastendruck neu — sonst
  // waere das, was man sich gerade notiert hat, schon wieder ein anderes.
  React.useEffect(() => {
    if (!adminPasswort) setAdminPasswort(passwortErzeugen());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Die gewaehlte Vorlage — sie sagt, ob das Produkt ueberhaupt ein
  // Betreiberkonto kennt und unter welchem Schluessel dessen Passwort liegt.
  const gewaehlteVorlage = vorlagen?.find((v) => v.id === templateId);

  const produkt = produkte?.find((p) => p.id === productId);

  // Die Software, die hier entsteht: ihr Slug steuert Demo-Inhalte,
  // Stackname und wer bei einer Verknuepfung Wirt ist — nicht das Produkt.
  const appSlug = teil?.application.slug ?? produkt?.slug ?? '';

  // Einstellungsfelder der Application (z. B. eLibrary: Demo-Set, Marke,
  // Federation-Token) — landen in currentConfig und von dort in die .env.
  // Vorbelegt mit den Vorgaben des Produkts fuer den gewaehlten Plan, damit
  // man sieht, was das Paket mitbringt, und es hier noch aendern kann.
  const konfigFelder: ProductConfigField[] = teil?.application.configFields ?? [];
  const [konfig, setKonfig] = useState<Record<string, string>>({});
  useEffect(() => {
    const v = teil?.vorgaben ?? {};
    setKonfig(startWerte(konfigFelder, { ...(v.alle ?? {}), ...((planId && v.plaene?.[planId]) || {}) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId, applicationId, planId, konfigFelder.length]);
  const konfigFehlt = fehlendePflicht(konfigFelder, konfig);

  // ══ DEMO-INHALTE ═════════════════════════════════════════════════════════
  //
  // Nur bei Demo und Test. Der Katalog haengt am Produkt, nicht an der Instanz
  // — die gibt es hier noch nicht.
  const demoMoeglich = umgebung === 'DEMO' || umgebung === 'TEST';
  const { data: saatgut } = useQuery({
    queryKey: ['demo-saatgut', 'katalog', appSlug],
    queryFn: () => demoSaatgut.katalog(appSlug),
    enabled: Boolean(demoMoeglich && appSlug),
  });

  /**
   * Zwei Namen, zwei Regeln.
   *
   * **Subdomain** — was der Kunde in der Adresszeile sieht: `ancora`, bei
   * Abnahme `ancora-stage`. Das Produkt gehört nicht hinein; unter einer
   * Adresse steht eine Anwendung, und welche das ist, sagt die Anwendung
   * selbst.
   *
   * **Stack-Name** — was auf dem Wirt in `docker ps` steht:
   * `ancora-prod-bryve`. Dort liegen die Stacks mehrerer Kunden und mehrerer
   * Anwendungen nebeneinander, und wer dort sucht, braucht alle drei Angaben.
   *
   * Ohne hinterlegte Kurzform bleiben beide leer — aus dem Firmennamen einen
   * abzuleiten wäre geraten, und „ancora-verlagsservice-gmbh-co-kg" will
   * niemand als Subdomain.
   */
  const kuerzel = kunde?.shortCode ?? '';
  const umgebungKurz = umgebungDef(umgebung)?.kurz ?? 'prod';

  // Produktion bleibt ohne Zusatz -- die Hauptadresse traegt keinen Vermerk,
  // alles andere schon.
  const vorschlagSubdomain = kuerzel
    ? umgebung === 'PRODUCTION'
      ? kuerzel
      : `${kuerzel}-${umgebungKurz}`
    : '';

  const vorschlagStack =
    kuerzel && appSlug ? `${kuerzel}-${umgebungKurz}-${appSlug}` : '';

  useEffect(() => {
    if (subdomainVonHand || !vorschlagSubdomain) return;
    setSubdomain(vorschlagSubdomain);
  }, [vorschlagSubdomain, subdomainVonHand]);

  useEffect(() => {
    if (stackVonHand || !vorschlagStack) return;
    setStackName(vorschlagStack);
  }, [vorschlagStack, stackVonHand]);

  const gewaehlterServer = serverListe?.find((s) => s.id === serverId);

  /**
   * Nur Domains, bei denen Cloudflare tatsächlich aktiv ist.
   *
   * Zwei Quellen, und beide zählen: `cloudflareDetected` ist die Beobachtung
   * aus der Namensauflösung, `cloudflareTunnel` das, was ein Mensch bestätigt
   * hat. Die zweite ist die verlässlichere -- eine Zone kann bei Cloudflare
   * liegen, ohne dass die Erkennung sie erwischt.
   */
  const cloudflareDomains = (domainListe ?? []).filter(
    (d) => d.cloudflareDetected || d.cloudflareTunnel,
  );
  const domainAuswahl = alleDomains ? (domainListe ?? []) : cloudflareDomains;

  // ══ IM VERBUND MIT ANLEGEN ═══════════════════════════════════════════════
  //
  // Strategie ist, möglichst viel im Verbund anzubieten: zu ojfm gleich bryve
  // und die eLibrary, zu bryve oder eLibrary gleich ojfm. Wer das ankreuzt,
  // bekommt je Produkt einen Reiter mit Adresse, Betreiberkonto und — falls die
  // Anwendung welche verlangt — Einstellungen. Kunde, Server, Betriebsart und
  // Umgebung gelten für alle. Angelegt wird nacheinander, verknüpft danach mit
  // ojfm als Wirt; die Art der Brücke wählt der PC selbst.
  const VERBUND_PARTNER: Record<string, string[]> = { ojfm: ['bryve', 'elibrary'], bryve: ['ojfm'], elibrary: ['ojfm'] };
  const VERBUND_NAME: Record<string, string> = { ojfm: 'ojfm', bryve: 'bryve', elibrary: 'eLibrary' };
  const verbundMoeglich = art === 'PRODUKT' ? (VERBUND_PARTNER[appSlug] ?? []) : [];
  const [zusaetze, setZusaetze] = useState<VerbundZusatz[]>([]);
  const [reiter, setReiter] = useState<string>('haupt');

  // Wechselt das Hauptprodukt, passen die angehakten Partner nicht mehr.
  useEffect(() => {
    setZusaetze((z) => z.filter((x) => verbundMoeglich.includes(x.slug)));
    setReiter('haupt');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appSlug]);

  // Die eigenständigen Produkte der Partner (Produkt-Slug = Application-Slug)
  const verbundProdukt = (slug: string) => (produkte ?? []).find((p) => p.slug === slug);
  const verbundDetails = useQueries({
    queries: zusaetze.map((z) => ({
      queryKey: ['products', verbundProdukt(z.slug)?.id, 'detail'],
      queryFn: () => products.get(verbundProdukt(z.slug)!.id),
      enabled: Boolean(verbundProdukt(z.slug)),
    })),
  });
  const verbundApp = (i: number) => verbundDetails[i]?.data?.applications?.[0];
  const verbundVorlagen = useQueries({
    queries: zusaetze.map((_, i) => ({
      queryKey: ['applications', verbundApp(i)?.applicationId, 'templates'],
      queryFn: () => applications.getTemplates(verbundApp(i)!.applicationId),
      enabled: Boolean(verbundApp(i)),
    })),
  });
  const verbundVorlage = (i: number) => verbundVorlagen[i]?.data?.find((v) => v.status === 'ACTIVE');
  const verbundFelder = (i: number): ProductConfigField[] => verbundApp(i)?.application.configFields ?? [];

  // Welche Domain bei einem Produkt üblich ist (bryve: bryve.app, eLibrary:
  // juni.cloud …) — aus den bestehenden Instanzen gelesen, nicht festgeschrieben.
  const { data: alleProduktInstanzen } = useQuery({
    queryKey: ['instances', 'PRODUKT'],
    queryFn: () => instances.list({ art: 'PRODUKT' }),
    enabled: verbundMoeglich.length > 0,
  });
  const ueblicheDomain = (slug: string) => {
    const zaehler = new Map<string, number>();
    for (const i of alleProduktInstanzen ?? []) {
      const s = i.application?.slug ?? i.product?.slug;
      if (s === slug && i.domain) zaehler.set(i.domain, (zaehler.get(i.domain) ?? 0) + 1);
    }
    return [...zaehler.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? '';
  };

  const verbundAnhaken = (slug: string, an: boolean) => {
    if (!an) {
      setZusaetze((z) => z.filter((x) => x.slug !== slug));
      if (reiter === slug) setReiter('haupt');
      return;
    }
    setZusaetze((z) => [
      ...z,
      {
        slug,
        domainQuelle: 'unsere',
        domain: ueblicheDomain(slug) || domain,
        subdomain: vorschlagSubdomain,
        subdomainVonHand: false,
        stackName: kuerzel ? `${kuerzel}-${umgebungKurz}-${slug}` : '',
        stackVonHand: false,
        adminEmail: 'admin@juni.com',
        adminPasswort: passwortErzeugen(),
        konfig: {},
        konfigVorbelegt: false,
      },
    ]);
    setReiter(slug);
  };
  const zusatzSetzen = (slug: string, teil: Partial<VerbundZusatz>) =>
    setZusaetze((z) => z.map((x) => (x.slug === slug ? { ...x, ...teil } : x)));

  // Namen folgen Kürzel und Umgebung, solange niemand sie von Hand geändert hat.
  useEffect(() => {
    setZusaetze((z) => z.map((x) => ({
      ...x,
      subdomain: x.subdomainVonHand || !vorschlagSubdomain ? x.subdomain : vorschlagSubdomain,
      stackName: x.stackVonHand || !kuerzel ? x.stackName : `${kuerzel}-${umgebungKurz}-${x.slug}`,
    })));
  }, [vorschlagSubdomain, kuerzel, umgebungKurz]);

  // Einstellungen mit den Vorgaben des Produkts füllen, sobald sie geladen sind.
  const detailsGeladen = verbundDetails.map((d) => (d.data ? '1' : '0')).join('');
  useEffect(() => {
    setZusaetze((z) => z.map((x, i) => {
      const app = verbundApp(i);
      if (x.konfigVorbelegt || !app) return x;
      return { ...x, konfig: startWerte(app.application.configFields ?? [], app.vorgaben?.alle ?? {}), konfigVorbelegt: true };
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailsGeladen]);

  /** Adresse oder Stack-Name doppelt — innerhalb des Verbunds? */
  const verbundKollision = (x: VerbundZusatz): string | null => {
    const adresse = (s: string, d: string) => [s, d].filter(Boolean).join('.');
    const alle = [{ slug: appSlug, adresse: adresse(subdomain, domain), stack: stackName }, ...zusaetze.map((z) => ({ slug: z.slug, adresse: adresse(z.subdomain, z.domain), stack: z.stackName }))];
    const andere = alle.filter((a) => a.slug !== x.slug);
    if (x.domain && andere.some((a) => a.adresse === adresse(x.subdomain, x.domain)))
      return `Die Adresse ${adresse(x.subdomain, x.domain)} ist im Verbund schon vergeben — Subdomain oder Domain ändern.`;
    if (x.stackName && andere.some((a) => a.stack === x.stackName)) return `Der Stack-Name ${x.stackName} ist im Verbund schon vergeben.`;
    return null;
  };
  const verbundVollstaendig = zusaetze.every((x, i) =>
    verbundProdukt(x.slug)
    && verbundApp(i)
    && x.stackName.trim()
    && x.domain.trim()
    && !fehlendePflicht(verbundFelder(i), x.konfig).length
    && !verbundKollision(x),
  );

  const fehlerText = (err: unknown, rueckfall: string) =>
    (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? rueckfall;

  /** Was im Verbund angelegt wurde — bleibt stehen, falls etwas danach scheitert. */
  const [verbundErgebnis, setVerbundErgebnis] = useState<{ hauptId: string; zeilen: { name: string; id?: string; fehler?: string }[] } | null>(null);

  const zusaetzeAnlegen = async (hauptId: string) => {
    const zeilen: { name: string; id?: string; fehler?: string }[] = [];
    for (const [i, x] of zusaetze.entries()) {
      const name = VERBUND_NAME[x.slug] ?? x.slug;
      const vorlage = verbundVorlage(i);
      let id: string | undefined;
      try {
        const neu = (await instances.create({
          customerId,
          productId: verbundProdukt(x.slug)!.id,
          applicationId: verbundApp(i)!.applicationId,
          ...(hostingMode === 'MANAGED' && serverId ? { serverId } : {}),
          ...(vorlage ? { currentTemplateId: vorlage.id } : {}),
          ...(Object.values(x.konfig).some((v) => v.trim())
            ? { currentConfig: Object.fromEntries(Object.entries(x.konfig).filter(([, v]) => v.trim())) }
            : {}),
          domain: x.domain.trim(),
          ...(x.subdomain.trim() ? { subdomain: x.subdomain.trim() } : {}),
          stackName: x.stackName.trim(),
          releaseChannel,
          hostingMode,
          environment: umgebung,
          art,
          ...(vorlage?.adminSecretKey && x.adminPasswort.trim().length >= 8 ? { adminPassword: x.adminPasswort.trim() } : {}),
          ...(vorlage?.adminSecretKey && x.adminEmail.includes('@') ? { adminEmail: x.adminEmail.trim() } : {}),
        } as never)) as { id: string };
        id = neu.id;
      } catch (err) {
        zeilen.push({ name, fehler: `nicht angelegt: ${fehlerText(err, 'unbekannter Grund')}` });
        continue;
      }
      // ojfm ist der Wirt, das andere der Gast.
      try {
        if (appSlug === 'ojfm') await instances.createLink(hauptId, id);
        else if (x.slug === 'ojfm') await instances.createLink(id, hauptId);
        zeilen.push({ name, id });
      } catch (err) {
        zeilen.push({ name, id, fehler: `angelegt, aber nicht verknüpft: ${fehlerText(err, 'unbekannter Grund')} — auf der Instanzseite unter „Verknüpfungen" nachholbar` });
      }
    }
    // Ist die Hauptinstanz bryve oder eLibrary und kam kein ojfm dazu, bleibt es
    // bei der Verknüpfung aus der Auswahl unten (falls gewählt).
    return zeilen;
  };

  const anlegen = useMutation({
    mutationFn: () =>
      instances.create({
        customerId,
        productId,
        ...(applicationId ? { applicationId } : {}),
        ...(planId ? { planId } : {}),
        ...(hostingMode === 'MANAGED' && serverId ? { serverId } : {}),
        ...(templateId ? { currentTemplateId: templateId } : {}),
        ...(Object.values(konfig).some((v) => v.trim())
          ? { currentConfig: Object.fromEntries(Object.entries(konfig).filter(([, v]) => v.trim())) }
          : {}),
        ...(domain ? { domain } : {}),
        ...(subdomain ? { subdomain } : {}),
        ...(stackName ? { stackName } : {}),
        releaseChannel,
        hostingMode,
        environment: umgebung,
        art,
        ...(adminPasswort.trim().length >= 8 ? { adminPassword: adminPasswort.trim() } : {}),
        ...(adminEmail.includes('@') ? { adminEmail: adminEmail.trim() } : {}),
        ...(art === 'WEBSEITE'
          ? {
              composeInline,
              envInline,
              ...(zielDienst ? { zielDienst } : {}),
              ...(zielPort ? { zielPort: Number(zielPort) } : {}),
            }
          : {}),
        ...(demoMoeglich && demoSeedIds.length ? { demoSeedIds } : {}),
      } as never),
    onSuccess: async (neu: { id: string }) => {
      // Verknüpfen erst nach dem Anlegen -- vorher gibt es die Instanz nicht.
      // Schlägt es fehl, ist die Instanz trotzdem da: sie deshalb wieder zu
      // löschen wäre die schlechtere Antwort, die Verknüpfung lässt sich auf
      // der Instanzseite nachholen.
      if (verknuepfenMit) {
        const partner = kundenInstanzen?.find((i) => i.id === verknuepfenMit);
        // Wer bettet wen ein: ojfm ist der Wirt, das andere der Gast.
        const neueIstWirt = appSlug === 'ojfm';
        const wirt = neueIstWirt ? neu.id : verknuepfenMit;
        const gast = neueIstWirt ? verknuepfenMit : neu.id;
        try {
          await instances.createLink(wirt, gast);
        } catch (err) {
          setFehler(
            `Instanz „${partner?.stackName ?? ''}" angelegt, aber die Verknüpfung schlug fehl: ` +
              ((err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
                'unbekannter Grund') +
              ' — auf der Instanzseite unter „Verknüpfungen" nachholbar.',
          );
        }
      }
      if (zusaetze.length) {
        const zeilen = await zusaetzeAnlegen(neu.id);
        qc.invalidateQueries({ queryKey: ['instances'] });
        if (zeilen.some((z) => z.fehler)) {
          // Nicht weiterspringen: wer hier steht, muss sehen, was fehlt.
          setVerbundErgebnis({ hauptId: neu.id, zeilen });
          return;
        }
      }
      qc.invalidateQueries({ queryKey: ['instances'] });
      // Direkt zur Instanz: dort steht der Deploy-Knopf, und der ist der
      // nächste Schritt. Zur Liste zurückzuwerfen hiesse, ihn suchen zu lassen.
      navigate(`/instances/${neu.id}`);
    },
    onError: (err: unknown) =>
      setFehler(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          'Anlegen fehlgeschlagen.',
      ),
  });

  // ══ OHNE DOMAIN GEHT NICHTS ═════════════════════════════════════════════
  //
  // Eine Instanz ohne Domain laesst sich anlegen, aber nicht sinnvoll
  // ausrollen: der Proxy braucht einen Namen, das Zertifikat auch, und der
  // DDNS-Beiwagen weiss sonst nicht, welchen A-Record er halten soll. Was
  // dabei herauskam, war eine Instanz, die lief und die niemand erreichte.
  //
  // Die Pruefung steht zusaetzlich im Backend (instances.routes.ts): dieser
  // Dialog ist nicht der einzige Weg, auf dem eine Instanz entsteht.
  const vollstaendig =
    customerId
    && productId
    // Bei Webseiten gibt es keine Wahl; sonst muss feststehen, welche
    // Application entsteht (bei Buendeln ausdruecklich gewaehlt).
    && (art === 'WEBSEITE' || applicationId || teile.length <= 1)
    && !konfigFehlt.length
    && (hostingMode === 'SELF_HOSTED' || serverId)
    && stackName
    && domain.trim()
    && verbundVollstaendig;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4">
      <div className="mt-8 flex max-h-[88vh] w-full max-w-2xl flex-col overflow-y-auto rounded-xl bg-surface shadow-xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <div>
            <h2 className="text-base font-semibold text-slate-900">Instanz anlegen</h2>
            <p className="text-xs text-slate-500">
              Legt den Datensatz an. Ausgerollt wird danach, mit dem Deploy-Knopf.
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          >
            <X size={18} />
          </button>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            setFehler('');
            // ══ LEERE SUBDOMAIN IST SELTEN GEWOLLT ══════════════════════
            //
            // Ohne Subdomain laeuft die Instanz auf der Hauptdomain selbst --
            // also auf „juni.com" und nicht auf „kunde.juni.com". Das kommt
            // vor (die eigene Webseite), ist aber fast nie gemeint, und
            // auffallen wuerde es erst, wenn die Hauptdomain ploetzlich diese
            // Instanz zeigt.
            if (!subdomain.trim()) {
              const weiter = window.confirm(
                `Ohne Subdomain laeuft die Instanz direkt auf „${domain}".\n\n`
                  + `Die Hauptdomain zeigt dann auf diese Instanz — nicht auf das, was `
                  + `dort bisher liegt.\n\nIst das so gewollt?`,
              );
              if (!weiter) return;
            }
            anlegen.mutate();
          }}
          className="space-y-3 px-6 py-4 text-sm"
        >
          {fehler && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
              {fehler}
            </div>
          )}

          {verbundErgebnis && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              <p className="font-medium">Die Hauptinstanz ist angelegt. Im Verbund:</p>
              <ul className="mt-1 space-y-0.5">
                {verbundErgebnis.zeilen.map((z) => (
                  <li key={z.name}>
                    <strong>{z.name}</strong>: {z.fehler ?? 'angelegt und verknüpft'}
                  </li>
                ))}
              </ul>
              <button
                type="button"
                onClick={() => navigate(`/instances/${verbundErgebnis.hauptId}`)}
                className="mt-2 font-medium text-brand-700 underline"
              >
                Zur Hauptinstanz
              </button>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="mb-1 block text-xs text-slate-500">Kunde *</span>
              <select
                required
                value={customerId}
                onChange={(e) => setCustomerId(e.target.value)}
                className="w-full rounded border border-slate-300 px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-brand-500"
              >
                <option value="">—</option>
                {(kunden ?? []).map((k) => (
                  <option key={k.id} value={k.id}>
                    {k.companyName}
                  </option>
                ))}
              </select>
            </label>

            <label className="block">
              <span className="mb-1 block text-xs text-slate-500">Produkt *</span>
              <select
                required
                value={productId}
                onChange={(e) => {
                  setProductId(e.target.value);
                  setPlanId('');
                }}
                className="w-full rounded border border-slate-300 px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-brand-500"
              >
                <option value="">—</option>
                {auswahl.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </label>

            {/* Bei einem Buendel: welcher Teil diese Instanz wird. Jede
                Application des Buendels ist eine eigene Instanz; gehoeren zwei
                zusammen (ojfm + bryve), verknuepft der PC sie selbst. */}
            {teile.length > 1 && (
              <label className="block">
                <span className="mb-1 block text-xs text-slate-500">Application *</span>
                <select
                  required
                  value={applicationId}
                  onChange={(e) => setApplicationId(e.target.value)}
                  className="w-full rounded border border-slate-300 px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-brand-500"
                >
                  <option value="">— Teil des Bündels wählen</option>
                  {teile.map((t) => (
                    <option key={t.applicationId} value={t.applicationId}>
                      {t.application.name}
                    </option>
                  ))}
                </select>
              </label>
            )}

            <label className="block">
              <span className="mb-1 block text-xs text-slate-500">Plan</span>
              <select
                value={planId}
                onChange={(e) => setPlanId(e.target.value)}
                disabled={!productId}
                className="w-full rounded border border-slate-300 px-2 py-1.5 disabled:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-brand-500"
              >
                <option value="">— (ohne Plan)</option>
                {(plaene ?? []).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </label>

            <label className="block">
              <span className="mb-1 block text-xs text-slate-500">Vorlage</span>
              <select
                value={templateId}
                onChange={(e) => setTemplateId(e.target.value)}
                disabled={!applicationId}
                className="w-full rounded border border-slate-300 px-2 py-1.5 disabled:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-brand-500"
              >
                <option value="">—</option>
                {(vorlagen ?? []).map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.version} {v.status === 'ACTIVE' ? '(aktiv)' : `(${v.status?.toLowerCase()})`}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {konfigFelder.some((f) => f.visibleInPC) && (
            <div className="rounded-lg border border-slate-200 p-3">
              <div className="mb-2 text-xs font-semibold text-slate-600">Einstellungen des Produkts</div>
              <ProduktKonfigFelder felder={konfigFelder} werte={konfig} setWerte={setKonfig} />
              {konfigFehlt.length > 0 && <p className="mt-2 text-xs text-amber-700">Noch offen: {konfigFehlt.join(', ')}</p>}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="mb-1 block text-xs text-slate-500">Betriebsart</span>
              <select
                value={hostingMode}
                onChange={(e) => setHostingMode(e.target.value as Betriebsart)}
                className="w-full rounded border border-slate-300 px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-brand-500"
              >
                <option value="MANAGED">Von uns betrieben (SSH)</option>
                <option value="SELF_HOSTED">Beim Kunden (Aktivierung)</option>
              </select>
            </label>

            <label className="block">
              <span className="mb-1 block text-xs text-slate-500">
                Server {hostingMode === 'MANAGED' && '*'}
              </span>
              <select
                value={serverId}
                onChange={(e) => setServerId(e.target.value)}
                disabled={hostingMode === 'SELF_HOSTED'}
                required={hostingMode === 'MANAGED'}
                className="w-full rounded border border-slate-300 px-2 py-1.5 disabled:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-brand-500"
              >
                <option value="">—</option>
                {(serverListe ?? [])
                  .filter((s) => s.status !== 'DECOMMISSIONED')
                  .filter((s) => !erlaubteServer.length || erlaubteServer.includes(s.id))
                  .map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.hostname} · {s.activeSlots ?? 0}/{s.maxSlots ?? 0} belegt
                    </option>
                  ))}
              </select>
            </label>
          </div>

          {/* Was beim Ausrollen scheitern würde, gehört vorher gesagt und nicht
              in eine Fehlermeldung nach zwei Minuten. */}
          {gewaehlterServer && !gewaehlterServer.ssh?.hasKey && (
            <p className="flex gap-2 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-2 text-xs text-amber-800">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" />
              Für „{gewaehlterServer.hostname}" ist kein SSH-Schlüssel hinterlegt. Anlegen geht,
              Ausrollen nicht — den Schlüssel vorher unter Server eintragen.
            </p>
          )}

          {/* Nur bei Poolmaschinen. Bei einer dedizierten oder kundeneigenen
              Maschine steht die Slotzahl auf 1, weil dort ein Mandant vorgesehen
              ist -- eine zweite Instanz ist dann kein Kapazitaetsproblem,
              sondern der Normalfall (Produktion plus Abnahme). Die Warnung dort
              anzuzeigen hiesse, vor etwas zu warnen, das niemanden hindert:
              `maxSlots` wird nirgends geprueft. */}
          {gewaehlterServer &&
            gewaehlterServer.ownership === 'SHARED' &&
            (gewaehlterServer.activeSlots ?? 0) >= (gewaehlterServer.maxSlots ?? 0) && (
              <p className="flex gap-2 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-2 text-xs text-amber-800">
                <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                Diese Poolmaschine ist nach der hinterlegten Kapazität voll. Das hindert
                niemanden — es ist aber der Grund, warum später jemand sucht, warum es eng wird.
              </p>
            )}

          {/* ── Umgebung ────────────────────────────────────────────── */}
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-xs text-slate-500">{t('environment.label')}</span>
            <div className="flex flex-wrap gap-1">
              {UMGEBUNGEN.map((u) => (
                <button
                  key={u.wert}
                  type="button"
                  title={t(`environment.${u.schluessel}Hint`)}
                  onClick={() => setUmgebung(u.wert)}
                  className={`rounded px-2.5 py-1 text-xs font-medium ${
                    umgebung === u.wert
                      ? 'bg-brand-600 text-white'
                      : 'border border-slate-300 text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {t(`environment.${u.schluessel}`)}
                </button>
              ))}
            </div>

            {customerId && !kunde?.shortCode && (
              <span className="text-xs text-amber-700">
                Für diesen Kunden ist keine Kurzform hinterlegt — Namen bitte selbst eintragen.
              </span>
            )}
            {(subdomainVonHand || stackVonHand) && (vorschlagSubdomain || vorschlagStack) && (
              <button
                type="button"
                onClick={() => {
                  setSubdomainVonHand(false);
                  setStackVonHand(false);
                  if (vorschlagSubdomain) setSubdomain(vorschlagSubdomain);
                  if (vorschlagStack) setStackName(vorschlagStack);
                }}
                className="text-xs text-brand-700 underline"
              >
                Namen wieder ableiten
              </button>
            )}
          </div>

          {/* ── Webseite: compose.yaml, .env, Ziel ───────────────────────
              Keine Vorlage, sondern ein Text. Der PC haengt die
              npm.proxy-Labels an den gewaehlten Dienst — wer sie selbst
              schreiben muesste, muesste die Konvention kennen, und ein
              Tippfehler darin ergibt eine Seite ohne Proxy-Eintrag. */}
          {art === 'WEBSEITE' && (
            <div className="space-y-3 rounded-lg border border-slate-200 p-3">
              <label className="block">
                <span className="text-xs font-medium text-slate-700">compose.yaml *</span>
                <textarea
                  required
                  rows={10}
                  spellCheck={false}
                  value={composeInline}
                  onChange={(e) => setComposeInline(e.target.value)}
                  placeholder={'services:\n  web:\n    image: nginx:alpine\n    restart: unless-stopped'}
                  className="mt-1 w-full rounded border border-slate-300 px-2 py-1.5 font-mono text-xs focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
              </label>

              <label className="block">
                <span className="text-xs font-medium text-slate-700">
                  .env <span className="font-normal text-slate-400">(optional)</span>
                </span>
                <textarea
                  rows={4}
                  spellCheck={false}
                  value={envInline}
                  onChange={(e) => setEnvInline(e.target.value)}
                  placeholder={'DB_PASSWORD=…\n# {{STACK_NAME}} und {{DOMAIN}} setzt der PC ein'}
                  className="mt-1 w-full rounded border border-slate-300 px-2 py-1.5 font-mono text-xs focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
                <span className="mt-1 block text-xs text-slate-400">
                  Getrennt von der compose.yaml, damit Passwörter nicht im Klartext darin stehen.
                  Die Platzhalter des PC — <code className="font-mono">{'{{STACK_NAME}}'}</code>,{' '}
                  <code className="font-mono">{'{{DOMAIN}}'}</code> — gelten hier auch.
                </span>
              </label>

              {composeBefund && composeBefund.einwaende.length > 0 && (
                <ul className="space-y-1 rounded bg-amber-50 px-2 py-1.5">
                  {composeBefund.einwaende.map((e, i) => (
                    <li key={i} className="text-xs leading-snug text-amber-800">{e}</li>
                  ))}
                </ul>
              )}

              <div className="flex flex-wrap items-end gap-2">
                <label className="min-w-40 flex-1">
                  <span className="text-xs font-medium text-slate-700">Domain zeigt auf</span>
                  <select
                    value={zielDienst}
                    onChange={(e) => {
                      setZielDienst(e.target.value);
                      const d = composeBefund?.dienste.find((x) => x.name === e.target.value);
                      // Nur vorbelegen, nicht festlegen: viele Images lauschen
                      // auf 80, ohne es in der compose.yaml zu sagen.
                      if (d?.ports.length === 1) setZielPort(String(d.ports[0]));
                    }}
                    className="mt-1 w-full rounded border border-slate-300 px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-brand-500"
                  >
                    <option value="">— Dienst wählen</option>
                    {(composeBefund?.dienste ?? []).map((d) => (
                      <option key={d.name} value={d.name}>
                        {d.name}
                        {d.ports.length ? ` (${d.ports.join(', ')})` : ' — kein Port genannt'}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="w-24">
                  <span className="text-xs font-medium text-slate-700">Port</span>
                  <input
                    type="number"
                    min={1}
                    max={65535}
                    value={zielPort}
                    onChange={(e) => setZielPort(e.target.value)}
                    placeholder="80"
                    className="mt-1 w-full rounded border border-slate-300 px-2 py-1.5 font-mono focus:outline-none focus:ring-2 focus:ring-brand-500"
                  />
                </label>
              </div>
              {!zielDienst && composeInline.trim().length > 20 && (
                <p className="text-xs text-amber-700">
                  Ohne Dienst und Port bekommt der Stack keinen Proxy-Eintrag — er läuft dann, ist
                  aber unter der Adresse nicht erreichbar.
                </p>
              )}
            </div>
          )}


          {/* ── Verknüpfung ─────────────────────────────────────────── */}
          {customerId && (
            <div className="rounded-lg border border-slate-200 p-3">
              <p className="mb-2 flex items-center gap-1.5 text-xs font-medium text-slate-600">
                <Link2 size={13} className="text-slate-400" /> Verknüpfung
              </p>

              {verbundMoeglich.length > 0 && (
                <div className="mb-3 rounded-md bg-brand-50/50 px-3 py-2">
                  <p className="text-xs font-medium text-slate-700">Im Verbund mit anlegen</p>
                  <div className="mt-1.5 flex flex-wrap gap-4">
                    {verbundMoeglich.map((slug) => {
                      const p = verbundProdukt(slug);
                      return (
                        <label key={slug} className={`flex items-center gap-1.5 text-sm ${p ? 'cursor-pointer text-slate-700' : 'text-slate-400'}`}>
                          <input
                            type="checkbox"
                            disabled={!p}
                            checked={zusaetze.some((x) => x.slug === slug)}
                            onChange={(e) => verbundAnhaken(slug, e.target.checked)}
                            className="rounded border-slate-300"
                          />
                          {VERBUND_NAME[slug] ?? slug}
                        </label>
                      );
                    })}
                  </div>
                  {zusaetze.length > 0 && (
                    <p className="mt-1.5 text-xs text-slate-500">
                      Je Produkt ein eigener Reiter unten für Einstellungen, Betreiberkonto und Adresse. Der PC legt
                      alle an und verknüpft sie mit ojfm als Wirt — danach braucht jede Instanz ihren Deploy.
                    </p>
                  )}
                </div>
              )}
              {(() => {
                const kandidaten = (kundenInstanzen ?? []).filter(
                  // Andere Software, nicht anderes Produkt: im Buendel haben
                  // beide Teile dasselbe Produkt.
                  (i) => (i.applicationId ?? i.productId) !== (applicationId || productId),
                );
                if (!productId) {
                  return <p className="text-xs text-slate-400">Erst ein Produkt wählen.</p>;
                }
                if (!kandidaten.length) {
                  return (
                    <p className="text-xs text-slate-400">
                      Dieser Kunde hat keine Instanz eines anderen Produkts — es gibt nichts zu
                      verknüpfen.
                    </p>
                  );
                }
                const neueIstWirt = appSlug === 'ojfm';
                return (
                  <>
                    <select
                      value={verknuepfenMit}
                      onChange={(e) => setVerknuepfenMit(e.target.value)}
                      className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                    >
                      <option value="">— keine Verknüpfung</option>
                      {kandidaten.map((i) => (
                        <option key={i.id} value={i.id}>
                          {i.stackName ?? i.id} ({i.product?.name ?? '?'})
                        </option>
                      ))}
                    </select>
                    {verknuepfenMit && (
                      <p className="mt-1.5 text-xs text-slate-500">
                        {neueIstWirt
                          ? 'Die neue Instanz bettet die gewählte ein.'
                          : 'Die gewählte Instanz bettet die neue ein.'}{' '}
                        Der Brückenschlüssel wird dabei erzeugt.{' '}
                        <strong>Beide Seiten brauchen danach einen Deploy</strong>, damit die
                        Werte in ihren Konfigurationen landen.
                      </p>
                    )}
                  </>
                );
              })()}
            </div>
          )}

          {/* ── Je Instanz: bei einem Verbund ein Reiter je Produkt ───────── */}
          {zusaetze.length > 0 && (
            <div role="tablist" aria-label="Instanzen im Verbund" className="flex flex-wrap gap-1 border-b border-slate-200">
              {[
                { slug: 'haupt', name: VERBUND_NAME[appSlug] ?? produkt?.name ?? 'Hauptinstanz', offen: false },
                ...zusaetze.map((x, i) => ({
                  slug: x.slug,
                  name: VERBUND_NAME[x.slug] ?? x.slug,
                  offen: !(x.stackName.trim() && x.domain.trim() && !fehlendePflicht(verbundFelder(i), x.konfig).length && !verbundKollision(x)),
                })),
              ].map((r) => (
                <button
                  key={r.slug}
                  type="button"
                  role="tab"
                  aria-selected={reiter === r.slug}
                  onClick={() => setReiter(r.slug)}
                  className={`-mb-px border-b-2 px-3 py-1.5 text-sm ${
                    reiter === r.slug ? 'border-brand-600 font-medium text-brand-700' : 'border-transparent text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {r.name}
                  {r.slug === 'haupt' && <span className="ml-1.5 text-[10px] uppercase tracking-wide text-slate-400">Haupt</span>}
                  {r.offen && <span className="ml-1.5 inline-block h-1.5 w-1.5 rounded-full bg-amber-500" title="Angaben fehlen noch" />}
                </button>
              ))}
            </div>
          )}

          {reiter === 'haupt' ? (
          <>
          {/* ══ BETREIBERKONTO ═══════════════════════════════════════════
              Das Konto, mit dem WIR hineinkommen, wenn der Kunde anruft.

              Ohne Eintrag erzeugt der PC ein Passwort — und dann sieht es
              niemand je, weil erzeugte Geheimnisse nicht angezeigt werden. Wer
              spaeter hineinmuss, erzeugt an der Instanz ein neues und bekommt
              es einmal gezeigt. Das geht, ist aber ein Umweg, wenn man ohnehin
              gerade anlegt.

              Nur bei Produktinstanzen und Apps: eine Webseite hat keine
              Vorlage und damit kein Betreiberkonto. */}
          {art !== 'WEBSEITE' && gewaehlteVorlage?.adminSecretKey && (
            <div className="rounded-lg border border-slate-200 p-3">
              <div className="text-xs font-medium text-slate-700">
                Betreiberkonto{' '}
                <span className="font-normal text-slate-400">
                  — unser Weg hinein, wenn der Kunde anruft
                </span>
              </div>

              <label className="mt-2 block">
                <span className="text-xs text-slate-500">Adresse</span>
                <input
                  type="email"
                  value={adminEmail}
                  onChange={(e) => setAdminEmail(e.target.value)}
                  placeholder="admin@juni.com"
                  className="mt-1 w-full rounded border border-slate-300 px-2 py-1.5 font-mono text-xs focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
              </label>

              <div className="mt-2 flex flex-wrap items-center gap-2">
                <input
                  type={passwortSichtbar ? 'text' : 'password'}
                  value={adminPasswort}
                  onChange={(e) => setAdminPasswort(e.target.value)}
                  spellCheck={false}
                  autoComplete="new-password"
                  className="min-w-56 flex-1 rounded border border-slate-300 px-2 py-1.5 font-mono text-xs focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
                <button
                  type="button"
                  onClick={() => setPasswortSichtbar((v) => !v)}
                  className="rounded border border-slate-300 px-2 py-1.5 text-xs text-slate-600 hover:bg-slate-50"
                >
                  {passwortSichtbar ? 'verbergen' : 'zeigen'}
                </button>
                <button
                  type="button"
                  onClick={() => setAdminPasswort(passwortErzeugen())}
                  className="rounded border border-slate-300 px-2 py-1.5 text-xs text-slate-600 hover:bg-slate-50"
                >
                  neu würfeln
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(adminPasswort);
                      setKopiert(true);
                      setTimeout(() => setKopiert(false), 1500);
                    } catch {
                      // Ohne Zwischenablage bleibt der Wert sichtbar und
                      // markierbar — eine Fehlermeldung waere hier lauter als
                      // das Problem.
                    }
                  }}
                  className="rounded border border-slate-300 px-2 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                >
                  {kopiert ? 'kopiert' : 'kopieren'}
                </button>
              </div>

              <p className="mt-2 text-xs leading-snug text-slate-400">
                Landet als{' '}
                <code className="font-mono">{gewaehlteVorlage.adminSecretKey}</code>{' '}
                in den Geheimnissen der Instanz.{' '}
                <strong>Jetzt notieren</strong> — danach wird es hier nicht mehr angezeigt.
                Leeren heißt: der PC erzeugt eins, das niemand zu sehen bekommt.
              </p>
              {adminPasswort.trim().length > 0 && adminPasswort.trim().length < 8 && (
                <p className="mt-1 text-xs text-amber-700">
                  Mindestens acht Zeichen — kürzer wird es nicht übernommen, und der PC erzeugt
                  wieder eins.
                </p>
              )}
            </div>
          )}


          {/* ── Demo-Inhalte ─────────────────────────────────────────────
              Nur bei Demo und Test. Die Auswahl wird vorgemerkt und erst nach
              der Bereitstellung eingespielt — vorher gibt es keine Datenbank,
              in die sie schreiben koennte. */}
          {demoMoeglich && (saatgut?.length ?? 0) > 0 && (
            <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3">
              <div className="text-xs font-medium text-slate-700">
                Demo-Inhalte einspielen <span className="font-normal text-slate-400">(optional)</span>
              </div>
              <div className="mt-2 space-y-1.5">
                {saatgut!.map((sg) => (
                  <label key={sg.id} className="flex cursor-pointer items-start gap-2">
                    <input
                      type="checkbox"
                      checked={demoSeedIds.includes(sg.id)}
                      onChange={(e) =>
                        setDemoSeedIds((v) =>
                          e.target.checked ? [...v, sg.id] : v.filter((x) => x !== sg.id),
                        )
                      }
                      className="mt-0.5 rounded border-slate-300"
                    />
                    <span className="min-w-0">
                      <span className="text-xs font-medium text-slate-700">
                        {sg.name}
                        {sg.zerstoerend && (
                          <span className="ml-1.5 rounded-full bg-red-50 px-1.5 py-0.5 text-[10px] font-medium text-red-700">
                            löscht vorhandene Inhalte
                          </span>
                        )}
                      </span>
                      <span className="block text-xs leading-snug text-slate-500">
                        {sg.beschreibung}
                      </span>
                    </span>
                  </label>
                ))}
              </div>
              <p className="mt-2 text-xs text-slate-400">
                Läuft etwa eine halbe Minute nach dem Ausrollen an. Später nachholen geht auf der
                Instanzseite jederzeit.
              </p>
            </div>
          )}
          {demoMoeglich && produkt && (saatgut?.length ?? 0) === 0 && (
            <p className="text-xs text-slate-400">
              Für {produkt.name} sind noch keine Demo-Inhalte hinterlegt.
            </p>
          )}

          {/* ── Adresse ─────────────────────────────────────────────── */}
          <div className="rounded-lg border border-slate-200 p-3">
            <p className="mb-2 flex items-center gap-1.5 text-xs font-medium text-slate-600">
              <Globe size={13} className="text-slate-400" /> Adresse
            </p>

            <div className="mb-2 flex gap-1">
              {(['unsere', 'kunde'] as const).map((q) => (
                <button
                  key={q}
                  type="button"
                  onClick={() => {
                    setDomainQuelle(q);
                    setDomain('');
                  }}
                  className={`rounded px-2.5 py-1 text-xs font-medium ${
                    domainQuelle === q
                      ? 'bg-brand-600 text-white'
                      : 'border border-slate-300 text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {q === 'unsere' ? 'Unsere Domain' : 'Domain des Kunden'}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-3 gap-3">
              <label className="col-span-2 block">
                <span className="mb-1 block text-xs text-slate-500">Domain</span>
                {domainQuelle === 'unsere' ? (
                  <DomainAuswahl
                    domains={domainAuswahl}
                    wert={domain}
                    onChange={setDomain}
                    nurCloudflare={!alleDomains}
                    onNurCloudflareChange={(nur) => setAlleDomains(!nur)}
                    gesamtAnzahl={domainListe?.length ?? 0}
                    cloudflareAnzahl={cloudflareDomains.length}
                  />
                ) : (
                  <input
                    value={domain}
                    onChange={(e) => setDomain(e.target.value)}
                    placeholder="verlag-kunde.de"
                    className="w-full rounded border border-slate-300 px-2 py-1.5 font-mono text-xs focus:outline-none focus:ring-2 focus:ring-brand-500"
                  />
                )}
              </label>

              <label className="block">
                <span className="mb-1 block text-xs text-slate-500">Subdomain</span>
                <input
                  value={subdomain}
                  onChange={(e) => {
                    setSubdomainVonHand(true);
                    setSubdomain(e.target.value);
                  }}
                  placeholder="kunde"
                  className={`w-full rounded border px-2 py-1.5 font-mono text-xs focus:outline-none focus:ring-2 focus:ring-brand-500 ${
                    domain && !subdomain.trim()
                      ? 'border-amber-400 bg-amber-50'
                      : 'border-slate-300'
                  }`}
                />
                {domain && !subdomain.trim() && (
                  <span className="mt-1 block text-[11px] text-amber-700">
                    leer = direkt auf {domain}
                  </span>
                )}
              </label>
            </div>

            {/* Was daraus wird -- und was danach von selbst passiert. */}
            {(domain || subdomain) && (
              <p className="mt-2 text-xs text-slate-600">
                Erreichbar unter{' '}
                <span className="font-mono font-medium text-slate-800">
                  {[subdomain, domain].filter(Boolean).join('.')}
                </span>
              </p>
            )}

            {domainQuelle === 'unsere' &&
              domain &&
              domainListe?.length &&
              !cloudflareDomains.some((d) => d.name === domain) && (
                <p className="mt-1.5 flex gap-2 rounded bg-amber-50 px-2 py-1.5 text-xs text-amber-800">
                  <AlertTriangle size={13} className="mt-0.5 shrink-0" />
                  Bei „{domain}" ist kein Cloudflare erkannt. Dann setzt der DDNS-Container
                  den A-Record <strong>nicht</strong> — er muss von Hand angelegt und beim
                  Serverwechsel nachgezogen werden.
                </p>
              )}

            {domainQuelle === 'unsere' ? (
              <p className="mt-1.5 text-xs text-slate-400">
                Der A-Record wird vom DDNS-Container des Stacks selbst gesetzt und aktuell
                gehalten — deshalb kann die Instanz später den Server wechseln, ohne dass jemand
                DNS anfasst. Der Proxy-Eintrag samt Zertifikat entsteht über den Label-Sync.
                Voraussetzung: die Zone liegt bei Cloudflare und unser Token darf dort schreiben.
              </p>
            ) : (
              <DnsAnleitung
                hostname={[subdomain, domain].filter(Boolean).join('.') || null}
                domain={domain || null}
                ipv4={gewaehlterServer?.ipAddress ?? null}
                ipv6={gewaehlterServer?.ipv6Address ?? null}
                serverName={gewaehlterServer?.hostname ?? null}
              />
            )}
          </div>

          <label className="block">
            <span className="mb-1 block text-xs text-slate-500">Stack-Name *</span>
            <input
              required
              value={stackName}
              onChange={(e) => {
                setStackVonHand(true);
                setStackName(e.target.value);
              }}
              placeholder="kunde"
              className="w-full rounded border border-slate-300 px-2 py-1.5 font-mono text-xs focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </label>


          <p className="flex gap-2 text-xs text-slate-400">
            <Info size={13} className="mt-0.5 shrink-0" />
            Schema <span className="font-mono">kurzform-umgebung-anwendung</span>. Taucht in den
            Containernamen auf dem Host auf
            {appSlug ? ` (${appSlug}-backend-${stackName || 'name'})` : ''} und lässt sich
            später nicht mehr ohne Umzug ändern.
          </p>
          </>
          ) : (
            zusaetze.map((x, i) => x.slug === reiter && (
              <VerbundZusatzFelder
                key={x.slug}
                zusatz={x}
                setzen={(teil) => zusatzSetzen(x.slug, teil)}
                name={VERBUND_NAME[x.slug] ?? x.slug}
                domainListe={domainListe ?? []}
                konfigFelder={verbundFelder(i)}
                konfigFehlt={fehlendePflicht(verbundFelder(i), x.konfig)}
                adminSecretKey={verbundVorlage(i)?.adminSecretKey ?? null}
                passwortErzeugen={passwortErzeugen}
                kollision={verbundKollision(x)}
              />
            ))
          )}


          <div className="flex items-center justify-end gap-2 border-t border-slate-100 pt-3">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 text-sm text-slate-500 hover:text-slate-700"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={!vollstaendig || anlegen.isPending || Boolean(verbundErgebnis)}
              className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
            >
              {anlegen.isPending ? 'Legt an…' : 'Anlegen'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
